import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ApiError, NetworkError } from "../api/client";
import { resetMemberPassword, type CredentialResetTarget, type ResetPasswordResponse } from "../api/members";
import { useAuth } from "../auth/AuthContext";
import { useCredentialRevealGuard } from "../auth/CredentialRevealGuard";
import { Button } from "../components/Button";
import { CredentialRevealPanel } from "../components/credentials/CredentialRevealPanel";
import { StatusBanner } from "../components/StatusBanner";

function handleResetFailure(
  cause: unknown,
  pathname: string,
  expireSession: () => void,
  navigate: (path: string) => void,
  setRetryRequired: (value: boolean) => void,
  setError: (value: string) => void,
): void {
  if (cause instanceof ApiError && cause.status === 401) {
    expireSession();
    return;
  }
  if (cause instanceof ApiError && cause.code === "mfa_elevation_required") {
    navigate(`/mfa/desafio?retorno=${encodeURIComponent(pathname)}`);
    return;
  }
  setRetryRequired(cause instanceof ApiError && cause.code === "operation_failed_previously");
  setError(cause instanceof ApiError || cause instanceof NetworkError
    ? cause.message
    : "Não foi possível redefinir a senha.");
}

// This screen has two target modes and three explicit credential-result states; keeping those
// transitions together makes the irreversible-password UX grepable and auditable.
// eslint-disable-next-line complexity
export function SupportCredentialsPage() {
  const { memberId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { state, expireSession } = useAuth();
  const { clearReveal, guardNavigation } = useCredentialRevealGuard();
  const [targetKind, setTargetKind] = useState<"member" | "login_identity">("member");
  const [targetId, setTargetId] = useState(memberId ?? "");
  const [result, setResult] = useState<ResetPasswordResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryRequired, setRetryRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const operationId = useRef(crypto.randomUUID());

  useEffect(() => () => clearReveal(), [clearReveal]);

  if (state.status !== "authenticated") return null;
  const accessToken = state.accessToken;
  const canReset = Boolean(state.sessionContext?.capabilities.reset_password);

  const target: CredentialResetTarget = targetKind === "member"
    ? { member_id: targetId.trim() }
    : { login_identity_id: targetId.trim() };

  function changeTarget(kind: "member" | "login_identity", value = "") {
    setTargetKind(kind);
    setTargetId(value);
    operationId.current = crypto.randomUUID();
    setResult(null);
    setError(null);
    setRetryRequired(false);
  }

  async function reset(newOperation = false) {
    if (!canReset) return;
    if (newOperation) operationId.current = crypto.randomUUID();
    setSaving(true); setError(null); setResult(null); setRetryRequired(false);
    try {
      setResult(await resetMemberPassword(accessToken, target, operationId.current));
    } catch (cause) {
      handleResetFailure(cause, location.pathname, expireSession, navigate, setRetryRequired, setError);
    } finally { setSaving(false); }
  }

  return (
    <main className="operational-page">
      {memberId && <Link className="page-back" to={`/associados/${memberId}`} onClick={guardNavigation}>Voltar para ficha</Link>}
      <section className="form-card" aria-labelledby="support-password-title">
        <p className="members-page__eyebrow">Suporte técnico</p>
        <h1 id="support-password-title">Redefinir senha</h1>
        <p className="form-card__intro">Emita uma nova senha temporária para um associado ou conta técnica. A senha exigirá troca no próximo acesso.</p>
        {!canReset && <StatusBanner tone="warning">Seu acesso não possui permissão para redefinir senhas.</StatusBanner>}
        {!memberId && <div className="support-target">
          <label htmlFor="support-target-kind">Tipo de alvo</label>
          <select id="support-target-kind" value={targetKind} onChange={(event) => changeTarget(event.target.value as "member" | "login_identity")}>
            <option value="member">Associado</option>
            <option value="login_identity">Conta técnica</option>
          </select>
          <label htmlFor="support-target-id">{targetKind === "member" ? "ID do associado" : "ID da identidade técnica"}</label>
          <input id="support-target-id" value={targetId} onChange={(event) => changeTarget(targetKind, event.target.value)} />
        </div>}
        {error && <StatusBanner tone="error">{error}</StatusBanner>}
        {retryRequired && canReset && <Button type="button" variant="secondary" onClick={() => void reset(true)}>Gerar nova senha temporária</Button>}
        {result?.status === "completed" ? (
          <CredentialRevealPanel password={result.temporary_password} />
        ) : result ? <>
          <StatusBanner tone="info">{result.message}</StatusBanner>
          {canReset && <Button type="button" loading={saving} disabled={!targetId.trim()} onClick={() => void reset(true)}>Gerar nova senha temporária</Button>}
        </> : null}
        {canReset && !result && <Button type="button" loading={saving} disabled={!targetId.trim()} onClick={() => void reset()}>Gerar senha temporária</Button>}
      </section>
    </main>
  );
}
